import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MESES, diasDaSemana } from '@/shared/utils/datas';
import { LinhaHistoricoLogin } from '@/modules/reports/solar.service';

type Props = {
  linhas: LinhaHistoricoLogin[];
  dataInicial: string;
  dataFinal: string;
};

function inicioDoMes(valor: string) {
  const [ano, mes] = valor.slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(ano) || !Number.isFinite(mes)) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  return new Date(ano, mes - 1, 1);
}

function chaveDia(data: Date) {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

function diaDaLinha(valor: string) {
  const match = valor.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return '';
  return `${match[3]}-${match[2]}-${match[1]}`;
}

function dataAmigavel(chave: string) {
  const [ano, mes, dia] = chave.split('-');
  return `${dia}/${mes}/${ano}`;
}

export function CalendarioHistoricoLogin({ linhas, dataInicial, dataFinal }: Props) {
  const [diaAberto, setDiaAberto] = useState<string | null>(null);

  const mesesVisiveis = useMemo(() => {
    const inicio = inicioDoMes(dataInicial);
    const fim = inicioDoMes(dataFinal);
    const meses: { mes: number; ano: number }[] = [];
    const cursor = new Date(inicio);
    while (cursor <= fim && meses.length < 12) {
      meses.push({ mes: cursor.getMonth(), ano: cursor.getFullYear() });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return meses;
  }, [dataInicial, dataFinal]);

  const porDia = useMemo(() => {
    const mapa = new Map<string, LinhaHistoricoLogin[]>();
    linhas.forEach((linha) => {
      const chave = diaDaLinha(linha.cadastrado_em);
      if (!chave) return;
      const lista = mapa.get(chave) ?? [];
      lista.push(linha);
      mapa.set(chave, lista);
    });
    return mapa;
  }, [linhas]);

  const hoje = useMemo(() => {
    const atual = new Date();
    return new Date(atual.getFullYear(), atual.getMonth(), atual.getDate()).getTime();
  }, []);

  const linhasDoDia = diaAberto ? porDia.get(diaAberto) ?? [] : [];

  function renderMes({ mes, ano }: { mes: number; ano: number }) {
    const diasNoMes = new Date(ano, mes + 1, 0).getDate();
    const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
    const dias: (number | null)[] = [];
    for (let i = 0; i < primeiroDiaSemana; i++) dias.push(null);
    for (let dia = 1; dia <= diasNoMes; dia++) dias.push(dia);

    return (
      <div key={`${ano}-${mes}`} className="calendario-mes-col">
        <div className="card h-100 calendario-card">
          <div className="card-header text-center bg-light py-1 px-2">
            <strong className="calendario-mes-titulo">{MESES[mes]} {ano}</strong>
          </div>
          <div className="card-body p-1">
            <div className="calendario-mes">
              <div className="calendario-dias-semana">
                {diasDaSemana.map((dia, index) => (
                  <div
                    key={dia}
                    className={`calendario-dia-semana${index === 0 || index === 6 ? ' fim-de-semana' : ''}`}
                  >
                    {dia}
                  </div>
                ))}
              </div>
              <div className="calendario-dias">
                {dias.map((dia, index) => {
                  if (dia === null) return <div key={index} className="calendario-dia vazio" />;
                  const data = new Date(ano, mes, dia);
                  const chave = chaveDia(data);
                  const logins = porDia.get(chave) ?? [];
                  const temLogin = logins.length > 0;
                  const isHoje = data.getTime() === hoje;
                  const isFimDeSemana = index % 7 === 0 || index % 7 === 6;
                  return (
                    <div
                      key={chave}
                      className={`calendario-dia ${isHoje ? 'hoje' : ''} ${temLogin ? 'com-evento' : ''}${isFimDeSemana ? ' fim-de-semana' : ''}`}
                      onClick={() => {
                        if (temLogin) setDiaAberto(chave);
                      }}
                      style={{ cursor: temLogin ? 'pointer' : 'default' }}
                      title={temLogin ? `${logins.length} login(s)` : ''}
                    >
                      <span className="calendario-dia-numero">{dia}</span>
                      {temLogin && <span className="calendario-dia-total">{logins.length}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relatorio-login-calendario mt-4">
      <h4 className="mb-2">Calendário de logins</h4>
      <div className="calendario-meses">
        {mesesVisiveis.map(renderMes)}
      </div>

      <Modal show={diaAberto != null} onHide={() => setDiaAberto(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Logins do dia{diaAberto ? ` - ${dataAmigavel(diaAberto)}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-striped table-hover mb-0">
            <thead>
              <tr>
                <th>Login</th>
                <th>Logout</th>
                <th>IP</th>
                <th>Navegador</th>
              </tr>
            </thead>
            <tbody>
              {linhasDoDia.map((linha, indice) => (
                <tr key={`${linha.cadastrado_em}-${indice}`}>
                  <td>{linha.cadastrado_em}</td>
                  <td>{linha.logout}</td>
                  <td>{linha.endereco_ip}</td>
                  <td>{linha.info_navegador}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setDiaAberto(null)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .relatorio-login-calendario .calendario-meses {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 12px;
        }
        .relatorio-login-calendario .calendario-mes-col {
          flex: 0 0 280px;
          width: 280px;
          max-width: 100%;
        }
        .relatorio-login-calendario .calendario-mes-titulo {
          font-size: 0.85rem;
        }
        .relatorio-login-calendario .calendario-mes {
          display: flex;
          flex-direction: column;
        }
        .relatorio-login-calendario .calendario-dias-semana,
        .relatorio-login-calendario .calendario-dias {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        .relatorio-login-calendario .calendario-dias-semana {
          margin-bottom: 3px;
        }
        .relatorio-login-calendario .calendario-dia-semana {
          text-align: center;
          font-weight: bold;
          font-size: 0.7rem;
          padding: 3px 1px;
          color: #666;
        }
        .relatorio-login-calendario .calendario-dia-semana.fim-de-semana {
          color: #c97a7a;
        }
        .relatorio-login-calendario .calendario-dia {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 1px;
          padding: 2px 1px 1px;
          border: 1px solid #e0e0e0;
          border-radius: 3px;
          min-height: 34px;
        }
        .relatorio-login-calendario .calendario-dia-numero {
          font-size: 0.75rem;
          line-height: 1;
          color: #555;
        }
        .relatorio-login-calendario .calendario-dia.fim-de-semana .calendario-dia-numero {
          color: #c97a7a;
        }
        .relatorio-login-calendario .calendario-dia-total {
          font-size: 0.65rem;
          font-weight: 700;
          line-height: 1;
          color: #06754b;
        }
        .relatorio-login-calendario .calendario-dia.vazio {
          border: none;
          background: transparent;
        }
        .relatorio-login-calendario .calendario-dia:hover:not(.vazio) {
          background-color: #f0f0f0;
        }
        .relatorio-login-calendario .calendario-dia.hoje {
          background-color: #ffff76;
          border-color: #e6e650;
        }
        .relatorio-login-calendario .calendario-dia.hoje .calendario-dia-numero {
          font-weight: 700;
          color: #333;
        }
        .relatorio-login-calendario .calendario-dia.com-evento {
          background-color: #eeeeee !important;
          border-color: #d0d0d0;
          color: inherit;
          font-weight: inherit;
        }
        .relatorio-login-calendario .calendario-dia.com-evento:hover {
          background-color: #e4e4e4 !important;
        }
      `}</style>
    </div>
  );
}
